import _ from "lodash";
import { getPathType } from "./Rollback";

// The types of parameter.
const VALUETYPE = {
  ARRAY: "array",
  OBJECT: "object",
  EMPTY_ARRAY: "empty_array",
  NULL: "null",
  UNDEFINED: "undefined",
};

// The types of differences.
export const CHANGETYPE = {
  VALUE_CHANGED: "value_changed",
  VALUE_ADDED: "value_added",
  VALUE_REMOVED: "value_removed",
  ARRAY_CHANGED: "array_changed",
  ARRAY_ADDED: "array_added",
  ARRAY_REMOVED: "array_removed",
  TYPE_DIFFERENT: "type_different",
};

export const RULE_ADD = "Rule Added";
export const RULE_REMOVE = "Rule Removed";

export const DISPLAYTYPE = {
  ADD: "add",
  REMOVE: "remove",
  CHANGE: "change",
  NO_CHANGE: "no_change",
};

export function getDisplayType(changeType) {
  if (changeType === "") return DISPLAYTYPE.NO_CHANGE;
  if ([CHANGETYPE.ARRAY_ADDED, CHANGETYPE.VALUE_ADDED].indexOf(changeType) >= 0)
    return DISPLAYTYPE.ADD;
  if (
    [CHANGETYPE.ARRAY_REMOVED, CHANGETYPE.VALUE_REMOVED].indexOf(changeType) >=
    0
  )
    return DISPLAYTYPE.REMOVE;
  return DISPLAYTYPE.CHANGE;
}

export function getDisplayTypeColor(type) {
  const displayType = getDisplayType(type);
  if (displayType === DISPLAYTYPE.ADD) return "border-green-500";
  if (displayType === DISPLAYTYPE.REMOVE) return "border-red-500";
  if (displayType === DISPLAYTYPE.CHANGE) return "border-orange-500";
  return "border-gray-500";
}

// Get the parameter type.
function getType(value) {
  if (_.isArray(value)) {
    return value.length > 0 ? VALUETYPE.ARRAY : VALUETYPE.EMPTY_ARRAY;
  }
  if (value === null) return VALUETYPE.NULL;
  if (value === undefined) return VALUETYPE.UNDEFINED;
  return typeof value;
}

// Check if the value exists.
// undefined, null, or an empty array are considered nonexistent.
function checkExist(type) {
  return (
    type !== VALUETYPE.UNDEFINED &&
    type !== VALUETYPE.NULL &&
    type !== VALUETYPE.EMPTY_ARRAY
  );
}

// Compare two arrays without considering the element order.
function compareArrays(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;
  return _.differenceWith(arr1, arr2, _.isEqual).length === 0;
}

export const UprSettingUniqIdMap = new Map()
  .set("", { id: "id" })
  .set("[].targetingSection.customs[]", { id: "keyId" });

export const UprSettingSortedArray = [
  "[].priceSection.priceSettings",
  "[].targetingSection.customs",
];

export const UprSegmentationUniqIdMap = new Map().set("", { id: "id" });

export const FirstLookUniqIdMap = new Map()
  .set("", {
    id: "id",
    condition: (item) => item.enabled !== false,
    order: "priority",
  })
  .set("[].targetingSection.customs[]", { id: "keyId" });

export const FirstLookSortedArray = [
  "[].priceSection.priceSettings",
  "[].targetingSection.customs",
];

export const CustomGroupRule = {
  key: "[id]",
  groups: ["[id].targetingSection", "[id].priceSection"],
};
export const CustomRuleAliasKey = "name";

export const UprSegmentationGroupRule = {
  key: "[id]",
  groups: [],
};
export const UprSegmentationRuleAliasKey = "name";

export const idRegex = /\[.*?\]/;

export function parsedDiffTitle({ path, from, to }) {
  const pathList = path.split(".");
  const { key } = getPathType(pathList[0]);
  if (pathList.length === 1 && key === "id") {
    if (_.isNil(from) || _.isEmpty(from)) {
      return RULE_ADD;
    } else if (_.isNil(to) || _.isEmpty(to)) {
      return RULE_REMOVE;
    }
  }
  return null;
}

export function showEmpty(value) {
  if (value === "None") return "None";

  if (value === null) return "Null";

  if (_.isArray(value) && _.isEmpty(value)) return "[]";

  if (value === "") return '"" (empty string)';

  if (value === undefined) return "- (missing key)";

  if (value === 0) return "0";

  if (value === false) return "false";

  return value;
}

export function compareTwoObjectsDiff(
  from,
  to,
  uniqIdMap = new Map(),
  sortedArray = []
) {
  const result = [];

  function compareDiff(from, to, path = "", diffs = []) {
    const fromType = getType(from);
    const toType = getType(to);

    const hasFrom = checkExist(fromType);
    const hasTo = checkExist(toType);

    // Case 1: Both parameters do not exist.
    if (!hasFrom && !hasTo) return;

    // Case 2: The parameter "to" does not exist.
    if (hasFrom && !hasTo) {
      let parsedPath = path;
      if (_.isArray(from) && uniqIdMap.has("") && parsedPath === "") {
        parsedPath = getUniqIdFromArray(uniqIdMap.get(""), from);
      }
      diffs.push({
        path: parsedPath,
        type:
          toType === VALUETYPE.EMPTY_ARRAY || fromType === VALUETYPE.ARRAY
            ? CHANGETYPE.ARRAY_REMOVED
            : CHANGETYPE.VALUE_REMOVED,
        from,
        to,
      });
      return;
    }

    // Case 3: The parameter "from" does not exist.
    if (!hasFrom && hasTo) {
      let parsedPath = path;
      if (_.isArray(to) && uniqIdMap.has("") && parsedPath === "") {
        parsedPath = getUniqIdFromArray(uniqIdMap.get(""), to);
      }
      diffs.push({
        path: parsedPath,
        type:
          fromType === VALUETYPE.EMPTY_ARRAY || toType === VALUETYPE.ARRAY
            ? CHANGETYPE.ARRAY_ADDED
            : CHANGETYPE.VALUE_ADDED,
        from,
        to,
      });
      return;
    }

    // Case 4: The types of the "from" and "to" parameters are different.
    if (fromType !== toType) {
      diffs.push({
        path,
        type: CHANGETYPE.TYPE_DIFFERENT,
        from,
        to,
      });
      return;
    }

    if (fromType === VALUETYPE.OBJECT) {
      // Case 5: Compare the two objects "from" and "to".
      const fromKeys = Object.keys(from);
      const toKeys = Object.keys(to);

      const allKeys = _.uniq([...fromKeys, ...toKeys]);

      allKeys.forEach((key) => {
        const newPath = path !== "" ? `${path}.${key}` : key;
        compareDiff(from[key], to[key], newPath, diffs);
      });
    } else if (fromType === VALUETYPE.ARRAY) {
      // Case 6: Compare the two arrays "from" and "to".
      const newPath = _.replace(path, /\[.*?\]/g, "[]");
      // Check if the comparison should be done based on the "unique key".
      const uniqKey = uniqIdMap.get(newPath) || {};
      if (_.isEmpty(uniqKey)) {
        // Check if the comparison should be done based on the "position".
        if (sortedArray.indexOf(newPath) >= 0) {
          const maxLegnth = Math.max(from.length, to.length);
          for (let i = 0; i < maxLegnth; i++) {
            const newPath = path !== "" ? `${path}[${i}]` : `[${i}]`;
            compareDiff(from[i], to[i], newPath, diffs);
          }
        } else if (!compareArrays(from, to)) {
          diffs.push({
            path,
            type: CHANGETYPE.ARRAY_CHANGED,
            from,
            to,
          });
        }
      } else {
        if (from.length > 0 || to.length > 0) {
          const { id, condition, order } = uniqKey;
          // _.keyBy([{ a: 1, b: 3 }, { a: 2, b: 4 }])
          // => { 1: {a: 1, b: 2}, 2: {a: 2, b: 4} }
          const fromKeyObj = _.keyBy(from, id);
          const toKeyObj = _.keyBy(to, id);

          const fromList = _.map(from, (item) => item[id]);
          const toList = _.map(to, (item) => item[id]);

          const allKeys = _.uniq([...fromList, ...toList]);

          allKeys.forEach((key) => {
            const newPath =
              path !== "" ? `${path}[${id}:${key}]` : `[${id}:${key}]`;
            if (order) {
              const parseFrom = _.filter(
                from,
                (item) => !condition || condition(item)
              );

              const parseTo = _.filter(
                to,
                (item) => !condition || condition(item)
              );

              const fromIdx = _.findIndex(
                parseFrom,
                (item) => item[id] === _.toNumber(key)
              );
              const toIdx = _.findIndex(
                parseTo,
                (item) => item[id] === _.toNumber(key)
              );

              compareDiff(
                fromKeyObj[key]
                  ? {
                      [order]: fromIdx < 0 ? "--" : fromIdx + 1,
                      ...fromKeyObj[key],
                    }
                  : undefined,
                toKeyObj[key]
                  ? { [order]: toIdx < 0 ? "--" : toIdx + 1, ...toKeyObj[key] }
                  : undefined,
                newPath,
                diffs
              );
            } else {
              compareDiff(fromKeyObj[key], toKeyObj[key], newPath, diffs);
            }
          });
        }
      }
    } else if (from !== to) {
      // Case 6: Compare the two parameter "from" and "to" directly.
      diffs.push({
        path,
        type: CHANGETYPE.VALUE_CHANGED,
        from,
        to,
      });
    }
  }

  compareDiff(from, to, "", result);

  return result;
}

// Combine the same paths into a single object.
// ex. { path: "a.b.c", from: 1, to: 2 }
// + { path: "a.b.d", from: 2, to: 3 }
// => { path: "a.b", from: { c: 1, d: 2 }, to: { c: 2, d: 3 } }
export function combineDiff(diffs) {
  return _.toPairs(
    _.groupBy(
      diffs.map((diff) => {
        const diffList = diff.path.split(".");
        return {
          ...diff,
          tempPath:
            diffList.length > 1 && !idRegex.test(_.last(diffList))
              ? diffList.slice(0, -1).join(".")
              : diff.path,
        };
      }),
      "tempPath"
    )
  ).map(([path, group]) =>
    group.length > 1
      ? {
          path,
          from: _.mapValues(
            _.keyBy(group, (item) => _.last(item.path.split("."))),
            "from"
          ),
          to: _.mapValues(
            _.keyBy(group, (item) => _.last(item.path.split("."))),
            "to"
          ),
          type: CHANGETYPE.VALUE_CHANGED,
        }
      : _.omit(group[0], "tempPath")
  );
}

function getUniqIdFromArray(uniqKey, list) {
  if (!_.isArray(list)) return "";
  if (list.length === 0) return "";
  const { id } = uniqKey;
  const item = _.get(list, [0, id]);
  return _.isNil(item) ? "" : `[${id}:${item}]`;
}

function splitTokens(str) {
  return str.match(/([^[\]]+|\[[^\]]*\])/g) || [];
}

function matchToken(pattern, value) {
  // property
  if (!pattern.startsWith("[")) {
    return pattern === value;
  }

  // []
  if (pattern === "[]") {
    return /^\[\d+\]$/.test(value);
  }

  // [id]
  const keyMatch = pattern.match(/^\[(\w+)\]$/);
  if (keyMatch) {
    const key = keyMatch[1];
    const regex = new RegExp(`^\\[${key}:[^\\]]+\\]$`);
    return regex.test(value);
  }

  return false;
}

// Match priciple:
// [id] => [id:10001]
// [] => [0]
// property => property
// property[][keyId] => customs[0][keyId:11965479]
export function checkPathMatch(path, match) {
  const pathList = path.split(".");
  const matchList = match.split(".");
  if (matchList.length > pathList.length) return [];

  for (let i = 0; i < matchList.length; i++) {
    const matchTokens = splitTokens(matchList[i]);
    const pathTokens = splitTokens(pathList[i]);
    if (matchTokens.length > pathTokens.length) return [];

    for (let j = 0; j < matchTokens.length; j++) {
      if (!matchToken(matchTokens[j], pathTokens[j])) {
        return [];
      }
    }
  }

  return pathList.slice(0, matchList.length);
}

// rule: { key: [id], groups: [[id].targetingSection, [id].priceSection] }
export function groupDiff(diffs, rule) {
  const byRuleKey = _.groupBy(diffs, (diff) => {
    const result = checkPathMatch(diff.path, rule.key);
    if (result.length === 0) return "other";
    return result.join(".");
  });
  const byGroup = _.mapValues(byRuleKey, (list) => {
    const result = {};
    _.forEach(list, (diff) => {
      const groupMatch = _.find(rule.groups, (groupPath) => {
        const result = checkPathMatch(diff.path, groupPath);
        return result.length > 0;
      });
      const matchedPath = checkPathMatch(
        diff.path,
        groupMatch || rule.key
      ).join(".");
      const restPath = diff.path.replace(matchedPath, "").replace(/^\./, "");
      const matchKey = groupMatch || "others";
      if (!result[matchKey]) result[matchKey] = [];

      result[matchKey].push({
        ...diff,
        match: matchedPath,
        rest: restPath,
      });
    });
    return result;
  });

  return byGroup;
}
